import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isEnter } from '../../utils/keyboard';
import i18n from '../../i18n';

/**
 * 오픈플로 공통 팝업 — 브라우저 기본 alert/confirm/prompt 대신 쓴다 (테마·폰트 통일, Playwright/자동화에서도 DOM 으로 다룰 수 있음).
 *
 *   import { showAlert, showConfirm, showPrompt } from '.../common/AppDialog';
 *   await showAlert('저장 실패: …');                                   // 확인 버튼 하나
 *   if (await showConfirm('저장한 뒤 실행할까요?', { okText: '저장 후 실행' })) …   // true/false
 *   const name = await showPrompt('새 폴더 이름', { defaultValue: '' });  // string | null
 *
 * App 루트에 <AppDialogHost /> 를 한 번 마운트해야 한다. 여러 요청은 순서대로(큐) 표시된다.
 */
export type DialogKind = 'alert' | 'confirm' | 'prompt' | 'choice';
export type DialogTone = 'info' | 'warning' | 'danger' | 'success';

export interface DialogOptions {
  title?: string;
  okText?: string;
  cancelText?: string;
  tone?: DialogTone;
  /** choice 전용 — 확인·취소 사이의 두 번째 선택 */
  altText?: string;
  /** prompt 전용 */
  defaultValue?: string;
  placeholder?: string;
}

interface Request {
  id: number;
  kind: DialogKind;
  message: React.ReactNode;
  options: DialogOptions;
  resolve: (v: any) => void;
}

let seq = 0;
let enqueue: ((r: Request) => void) | null = null;
const pending: Request[] = []; // 호스트가 아직 마운트되지 않았을 때 쌓아 둔다

function request(kind: DialogKind, message: React.ReactNode, options: DialogOptions = {}): Promise<any> {
  return new Promise((resolve) => {
    const r: Request = { id: ++seq, kind, message, options, resolve };
    if (enqueue) enqueue(r); else pending.push(r);
  });
}

export const showAlert = (message: React.ReactNode, options: DialogOptions = {}): Promise<void> => request('alert', message, options);
export const showConfirm = (message: React.ReactNode, options: DialogOptions = {}): Promise<boolean> => request('confirm', message, options);
export const showPrompt = (message: React.ReactNode, options: DialogOptions = {}): Promise<string | null> => request('prompt', message, options);
/** 세 갈래 — okText('ok') · altText('alt') · 취소(null). 예: 검증 후 저장 / 검증 없이 저장 / 취소 */
export const showChoice = (message: React.ReactNode, options: DialogOptions = {}): Promise<'ok' | 'alt' | null> => request('choice', message, options);

const TONE_BAR: Record<DialogTone, string> = { info: 'bg-blue-500', warning: 'bg-amber-500', danger: 'bg-red-500', success: 'bg-green-500' };
const TONE_ICON: Record<DialogTone, string> = { info: 'ℹ', warning: '⚠', danger: '✕', success: '✓' };
/** 기본 제목 — common:dialog.<kind> */
const defaultTitle = (kind: DialogKind) => String(i18n.t(`common:dialog.${kind === 'choice' ? 'confirm' : kind}`));

export const AppDialogHost: React.FC = () => {
  const [queue, setQueue] = useState<Request[]>([]);
  const [value, setValue] = useState('');
  const okRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    enqueue = (r) => setQueue((q) => [...q, r]);
    if (pending.length) { const items = pending.splice(0); setQueue((q) => [...q, ...items]); }
    return () => { enqueue = null; };
  }, []);

  const cur = queue[0];
  useEffect(() => {
    if (!cur) return;
    setValue(cur.options.defaultValue ?? '');
    const t = setTimeout(() => (cur.kind === 'prompt' ? inputRef.current?.focus() : okRef.current?.focus()), 0);
    return () => clearTimeout(t);
  }, [cur]);

  const finish = useCallback((result: any) => {
    if (!cur) return;
    cur.resolve(result);
    setQueue((q) => q.slice(1));
  }, [cur]);

  useEffect(() => {
    if (!cur) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); finish(cur.kind === 'alert' ? undefined : cur.kind === 'confirm' ? false : null); }
      else if (isEnter(e) && cur.kind !== 'prompt') { e.preventDefault(); finish(cur.kind === 'alert' ? undefined : cur.kind === 'choice' ? 'ok' : true); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [cur, finish]);

  if (!cur) return null;
  const tone: DialogTone = cur.options.tone || (cur.kind === 'confirm' || cur.kind === 'choice' ? 'warning' : 'info');
  const title = cur.options.title || defaultTitle(cur.kind);
  const cancel = () => finish(cur.kind === 'confirm' ? false : null);
  const ok = () => finish(cur.kind === 'alert' ? undefined : cur.kind === 'confirm' ? true : cur.kind === 'choice' ? 'ok' : value);

  return (
    // 확인·알림은 어떤 창보다 위 — 연결·로그·DAG 코드 창(z 10001) 아래에 깔려 누를 수 없었다(2026-10-07)
    <div className="fixed inset-0 z-[10050] flex items-center justify-center bg-black bg-opacity-40" role="dialog" aria-modal="true" aria-label={title} data-app-dialog={cur.kind}
      onMouseDown={() => { if (cur.kind === 'alert') finish(undefined); }}>
      <div className="bg-white rounded-lg shadow-2xl w-[420px] max-w-[92vw] overflow-hidden" onMouseDown={(e) => e.stopPropagation()}>
        <div className={`h-1 ${TONE_BAR[tone]}`} />
        <div className="px-5 pt-4 pb-3">
          <div className="flex items-start gap-3">
            <span className={`shrink-0 w-6 h-6 rounded-full text-white text-xs flex items-center justify-center ${TONE_BAR[tone]}`}>{TONE_ICON[tone]}</span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-gray-900 mb-1">{title}</div>
              <div className="text-sm text-gray-700 whitespace-pre-wrap break-words">{cur.message}</div>
              {cur.kind === 'prompt' && (
                <input ref={inputRef} className="mt-3 w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white" value={value}
                  placeholder={cur.options.placeholder} onChange={(e) => setValue(e.target.value)}
                  onKeyDown={(e) => { if (isEnter(e)) { e.preventDefault(); ok(); } }} />
              )}
            </div>
          </div>
        </div>
        <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
          {cur.kind !== 'alert' && (
            <button type="button" className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700" onClick={cancel}>
              {cur.options.cancelText || i18n.t('common:ui.cancel')}
            </button>
          )}
          {cur.kind === 'choice' && (
            <button type="button" className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700" onClick={() => finish('alt')}>
              {cur.options.altText}
            </button>
          )}
          <button ref={okRef} type="button" onClick={ok}
            className={`px-3 py-1.5 text-sm rounded text-white ${tone === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {cur.options.okText || i18n.t('common:ui.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppDialogHost;
